import * as Yup from 'yup';
import { MandatePayer } from 'api/quote';
import { createFormConfig, ValidationRules } from 'helpers/createFormConfig';

// Field Names
export const QuoteMandateFormFieldNames = {
  StartDate: 'startDate',
  Frequency: 'frequency',
  DueDate: 'dueDate',
  Payer: 'payer',
  BankAccount: 'bankAccount'
} as const;

export type QuoteMandateFormFieldNames =
  (typeof QuoteMandateFormFieldNames)[keyof typeof QuoteMandateFormFieldNames];

// Types
export interface QuoteMandateFormValues {
  startDate: string;
  frequency: string;
  dueDate: number | null;
  payer: MandatePayer;
  bankAccount: string;
}

// Validation Rules
export const quoteMandateValidationRules: ValidationRules<
  keyof QuoteMandateFormValues
> = {
  [QuoteMandateFormFieldNames.StartDate]: Yup.date()
    // An emptied date input holds '': read it as "not filled", not as an invalid date.
    .transform((value, originalValue) => (originalValue === '' ? undefined : value))
    .typeError('Date invalide')
    .required('La date de debut est requise')
    // Miroir de la règle back (UpdateDevisMandatAsync) : pas de date de début dans le passé.
    .min(
      new Date(new Date().setHours(0, 0, 0, 0)),
      'La date de début ne peut pas être dans le passé'
    ),
  [QuoteMandateFormFieldNames.Frequency]: Yup.string().required(
    'La frequence est requise'
  ),
  [QuoteMandateFormFieldNames.DueDate]: Yup.number().nullable(),
  [QuoteMandateFormFieldNames.Payer]: Yup.string()
    .oneOf(Object.values(MandatePayer))
    .required(),
  [QuoteMandateFormFieldNames.BankAccount]: Yup.string().required(
    'La selection de mandat est requise'
  )
};

// Form Config
const quoteMandateDefaultValues: Partial<QuoteMandateFormValues> = {
  // createFormConfig seeds null for a Yup.date() rule, but the field is a controlled date input
  // holding a "yyyy-MM-dd" string: an empty string is its empty state.
  [QuoteMandateFormFieldNames.StartDate]: '',
  [QuoteMandateFormFieldNames.Payer]: MandatePayer.Souscripteur
};

export const quoteMandateFormConfig = createFormConfig<QuoteMandateFormValues>(
  quoteMandateValidationRules as ValidationRules<keyof QuoteMandateFormValues>,
  quoteMandateDefaultValues
);

/**
 * Configuration du formulaire selon que l'assuré signe ou non son propre mandat.
 *
 * Quand le souscripteur du collectif règle pour tous, le bloc « compte de prélèvement »
 * n'est pas affiché : exiger l'IBAN rendrait alors le formulaire impossible à valider,
 * sur un champ que l'utilisateur ne voit même pas.
 */
export const createQuoteMandateFormConfig = (requiresOwnMandate: boolean) =>
  createFormConfig<QuoteMandateFormValues>(
    {
      ...quoteMandateValidationRules,
      [QuoteMandateFormFieldNames.BankAccount]: requiresOwnMandate
        ? quoteMandateValidationRules[QuoteMandateFormFieldNames.BankAccount]
        : Yup.string()
    } as ValidationRules<keyof QuoteMandateFormValues>,
    quoteMandateDefaultValues
  );
